// Client authoring layer over the shared Logic contract
// (src/engine/runtime/logic/contract.ts). The event/condition/action
// vocabulary and the document shape itself are canonical there - this file
// only adds what is genuinely Studio's own: display labels, the
// authored-rule shape (a name for the rule list), and the cutscene-flag
// gating of the action picker.
//
// Previously the whole contract was redefined here as a hand-maintained
// mirror of sstk-sunder-realtime/src/logic/document.ts, kept in step by
// discipline rather than by import. It is now imported from the engine on
// both sides (see that repo's own `@sstk/build-kit/logic` re-export), so a
// contract change only has to happen in one place.
//
// SF23.4: the 'play-cutscene' action kind is part of the shared contract
// ahead of any runtime executing it (see `isActionKindUnexecuted` in the
// engine module). Authoring a rule with this action kind today is safe (it
// is valid data), but the Logic mode UI keeps it out of the action picker
// (see EDITOR_ACTION_KINDS / logicFeatures.ts) and flags any rule that
// already carries it as a compatibility warning, so an author is not misled
// into shipping custom logic no runtime yet executes. See the coordination
// note in sstk-wiki's studio-feature-buildout-plan.md (SF23.4) for what
// needs to land before the flag flips on.

import {
  EXECUTABLE_ACTION_KINDS,
  LOGIC_ACTION_KINDS,
  LOGIC_COMPARISONS,
  LOGIC_DOCUMENT_VERSION,
  LOGIC_EVENT_KINDS,
  LOGIC_FACTS,
  NAMED_FACTS,
  NUMERIC_FACTS,
  QUEST_STATUSES,
  isActionKindUnexecuted,
  type LogicAction,
  type LogicActionKind,
  type LogicComparison,
  type LogicCondition,
  type LogicEventKind,
  type LogicFactName,
  type QuestStatus,
  type LogicRule,
} from '../../engine';
import { LOGIC_CUTSCENES_ENABLED } from '../config/logicFeatures';

export {
  LOGIC_ACTION_KINDS,
  LOGIC_COMPARISONS,
  LOGIC_DOCUMENT_VERSION,
  LOGIC_EVENT_KINDS,
  LOGIC_FACTS,
  NAMED_FACTS,
  NUMERIC_FACTS,
  QUEST_STATUSES,
};
export type { LogicAction, LogicActionKind, LogicComparison, LogicCondition, LogicEventKind, LogicFactName, QuestStatus };

export const LOGIC_EVENT_LABELS: Record<LogicEventKind, string> = {
  'round-start': 'Round starts',
  'round-end': 'Round ends',
  'player-join': 'Player joins',
  'player-leave': 'Player leaves',
  'score-change': 'Score changes',
  'trigger-enter': 'Player enters zone',
  'trigger-exit': 'Player leaves zone',
  tick: 'Every second',
};

export const COMPARISON_LABELS: Record<LogicComparison, string> = {
  eq: 'is',
  ne: 'is not',
  lt: 'is below',
  le: 'is at most',
  gt: 'is above',
  ge: 'is at least',
};

export const LOGIC_FACT_LABELS: Record<LogicFactName, string> = {
  round: 'Round number',
  'score-raiders': 'Raiders score',
  'score-wardens': 'Wardens score',
  'player-count': 'Player count',
  'event-team': 'Event team',
  'event-trigger': 'Event zone',
  'event-player': 'Event player',
  variable: 'Match variable',
  'item-count': 'Item count',
  'quest-status': 'Quest status',
};

export const LOGIC_ACTION_LABELS: Record<LogicActionKind, string> = {
  announce: 'Announce',
  'set-variable': 'Set variable',
  'add-variable': 'Add to variable',
  emit: 'Emit event',
  'give-item': 'Give item',
  'take-item': 'Take item',
  'play-cutscene': 'Play cutscene',
  'win-game': 'Win game',
  'lose-game': 'Lose game',
  'start-quest': 'Start quest',
  'complete-quest': 'Complete quest',
  'fail-quest': 'Fail quest',
};

/**
 * Action kinds a runtime actually executes today. Kept under its original
 * name here (the engine's own export is `EXECUTABLE_ACTION_KINDS`) since
 * this is the name the Logic mode UI has always known it by.
 */
export const SERVER_SUPPORTED_ACTION_KINDS = EXECUTABLE_ACTION_KINDS;

/**
 * True when no runtime executes this action kind yet - a rule that uses it
 * parses fine but produces no effect anywhere it runs (per-rule
 * compatibility warning; SF23.4).
 */
export const isActionKindUnsupported = isActionKindUnexecuted;

/**
 * Action kinds the Logic mode's action picker offers for a NEW selection.
 * Gated by feature flags for actions authored ahead of runtime support
 * (logicFeatures.ts) - an author cannot pick 'play-cutscene' until the flag
 * flips on, even though the type/contract already carries it (SF23.4).
 */
export const EDITOR_ACTION_KINDS: readonly LogicActionKind[] = LOGIC_CUTSCENES_ENABLED
  ? LOGIC_ACTION_KINDS
  : LOGIC_ACTION_KINDS.filter((k) => k !== 'play-cutscene');

/** A shared `LogicRule` plus the display name the Logic mode shows in its
 * list - the one field genuinely Studio's own, not part of the executed
 * contract (a runtime never reads it). */
export interface AuthoredLogicRule extends LogicRule {
  name: string;
}

export type LogicDocV2 = {
  version: typeof LOGIC_DOCUMENT_VERSION;
  rules: AuthoredLogicRule[];
};

export function describeCondition(c: LogicCondition): string {
  const namedFactWord = c.fact === 'variable' ? 'variable' : c.fact === 'item-count' ? 'item' : 'quest';
  const fact = NAMED_FACTS.includes(c.fact)
    ? `${namedFactWord} ${c.name ?? ''}`.trim()
    : c.fact.replace(/-/g, ' ');
  return `${fact} ${COMPARISON_LABELS[c.op]} ${String(c.value)}`;
}

export function describeAction(a: LogicAction): [string, string] {
  switch (a.kind) {
    case 'announce':
      return ['Announce', a.message];
    case 'set-variable':
      return ['Set variable', `${a.name} = ${a.value}`];
    case 'add-variable':
      return ['Add to variable', `${a.name} + ${a.value}`];
    case 'emit':
      return ['Emit event', a.event];
    case 'give-item':
      return ['Give item', `${a.name} x${a.count}`];
    case 'take-item':
      return ['Take item', `${a.name} x${a.count}`];
    case 'play-cutscene':
      return ['Play cutscene', a.cutsceneId];
    case 'win-game':
      return ['Win game', a.team ? `team ${a.team}` : 'any team'];
    case 'lose-game':
      return ['Lose game', a.team ? `team ${a.team}` : 'any team'];
    case 'start-quest':
      return ['Start quest', `${a.questId} - ${a.title}`];
    case 'complete-quest':
      return ['Complete quest', a.questId];
    case 'fail-quest':
      return ['Fail quest', a.questId];
  }
}

/** A fresh default action of the given kind, for the action kind selector. */
export function defaultAction(kind: LogicActionKind, itemIds: readonly string[]): LogicAction {
  switch (kind) {
    case 'announce':
      return { kind, message: 'Round underway.' };
    case 'set-variable':
      return { kind, name: 'counter', value: 0 };
    case 'add-variable':
      return { kind, name: 'counter', value: 1 };
    case 'emit':
      return { kind, event: 'custom-event' };
    case 'give-item':
    case 'take-item':
      return { kind, name: itemIds[0] ?? '', count: 1 };
    case 'play-cutscene':
      return { kind, cutsceneId: '' };
    case 'win-game':
    case 'lose-game':
      return { kind };
    case 'start-quest':
      return { kind, questId: 'quest', title: 'New quest' };
    case 'complete-quest':
    case 'fail-quest':
      return { kind, questId: 'quest' };
  }
}
